import React, { useState, useRef } from 'react';
import { Document } from '@/types/lead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Upload, 
  FileImage, 
  File, 
  Archive,
  Trash2,
  Link,
  Paperclip
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
  DialogBody
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { deleteDocument, uploadDocumentFile } from '@/services/leads/documentService';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog';

interface DocumentsTabContentProps {
  documents: Document[];
  leadId: string;
  onAddDocument: (name: string, url: string, type: string, description?: string) => Promise<Document | void>;
  onDocumentDeleted?: () => void;
  isAddDocumentOpen?: boolean;
  onAddDocumentOpenChange?: (open: boolean) => void;
}

const DocumentsTabContent: React.FC<DocumentsTabContentProps> = ({ 
  documents, 
  leadId,
  onAddDocument,
  onDocumentDeleted,
  isAddDocumentOpen: externalIsAddDocumentOpen,
  onAddDocumentOpenChange
}) => {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [internalIsAddDocumentOpen, setInternalIsAddDocumentOpen] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('url');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAddDocumentOpen = externalIsAddDocumentOpen !== undefined 
    ? externalIsAddDocumentOpen
    : internalIsAddDocumentOpen;

  const setIsAddDocumentOpen = (open: boolean) => {
    if (onAddDocumentOpenChange) {
      onAddDocumentOpenChange(open);
    } else {
      setInternalIsAddDocumentOpen(open);
    }
  };

  const getDocumentIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <FileText className="h-4 w-4 md:h-5 md:w-5 text-primary" />;
      case 'image':
        return <FileImage className="h-4 w-4 md:h-5 md:w-5 text-primary" />;
      case 'zip':
      case 'archive':
        return <Archive className="h-4 w-4 md:h-5 md:w-5 text-primary" />;
      default:
        return <File className="h-4 w-4 md:h-5 md:w-5 text-primary" />;
    }
  };

  const getDocumentType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase() || '';
    
    if (extension === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    if (['zip', 'rar', '7z'].includes(extension)) return 'archive';
    
    return 'document';
  };

  const isFormValid = () => {
    if (activeTab === 'url') {
      return documentName.trim() !== '' && documentUrl.trim() !== '';
    }
    return fileInputRef.current?.files && fileInputRef.current.files.length > 0;
  };

  const handleSubmitDocument = async () => {
    if (!isFormValid()) {
      toast({
        variant: "destructive",
        title: "Invalid form",
        description: activeTab === 'url' 
          ? "Please provide both a name and URL for the document."
          : "Please select a file to upload."
      });
      return;
    }

    setIsSubmitting(true);
    setUploadError(null);
    try {
      if (activeTab === 'url') {
        const type = getDocumentType(documentUrl);
        await onAddDocument(documentName, documentUrl, type, documentDescription);
      } else if (fileInputRef.current?.files && fileInputRef.current.files.length > 0) {
        const file = fileInputRef.current.files[0];
        
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const newProgress = prev + 10;
            if (newProgress >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return newProgress;
          });
        }, 300);
        
        try {
          const uploadedDocument = await uploadDocumentFile(leadId, file, documentDescription);
          
          clearInterval(progressInterval);
          setUploadProgress(100);
          
          onAddDocument(
            uploadedDocument.name, 
            uploadedDocument.url, 
            uploadedDocument.type, 
            uploadedDocument.description
          );
        } catch (error) {
          clearInterval(progressInterval);
          console.error("Upload error:", error);
          setUploadError((error as Error).message || "Failed to upload document");
          throw error;
        }
      }
      
      setDocumentName('');
      setDocumentUrl('');
      setDocumentDescription('');
      setIsAddDocumentOpen(false);
      setUploadProgress(0);
      
      toast({
        title: "Document added",
        description: "Your document has been successfully added."
      });
    } catch (error) {
      console.error("Error adding document:", error);
      setUploadError((error as Error).message || "Failed to upload document");
      toast({
        variant: "destructive",
        title: "Failed to add document",
        description: "There was an error adding your document. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await deleteDocument(leadId, documentId);
      
      toast({
        title: "Document deleted",
        description: "The document has been successfully removed."
      });
      
      if (onDocumentDeleted) {
        onDocumentDeleted();
      }
      
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        variant: "destructive",
        title: "Failed to delete document",
        description: "There was an error deleting the document. Please try again."
      });
    }
  };

  const handleDialogClose = () => {
    if (isSubmitting) return;
    
    setDocumentName('');
    setDocumentUrl('');
    setDocumentDescription('');
    setUploadProgress(0);
    setUploadError(null);
    setIsAddDocumentOpen(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 md:pb-4">
        <CardTitle className="text-lg md:text-xl">Documents</CardTitle>
        <Dialog open={isAddDocumentOpen} onOpenChange={setIsAddDocumentOpen}>
          <DialogTrigger asChild>
            <Button 
              size={isMobile ? "sm" : "default"} 
              className="h-8 md:h-10"
            >
              <Upload className="h-4 w-4 mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Document</DialogTitle>
              <DialogDescription>
                Add a document by uploading a file or providing a link.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 mb-5">
                <TabsTrigger value="file" className="flex items-center">
                  <Paperclip className="h-4 w-4 mr-2" />
                  Upload File
                </TabsTrigger>
                <TabsTrigger value="url" className="flex items-center">
                  <Link className="h-4 w-4 mr-2" />
                  Add URL
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="file" className="space-y-5">
                <div className="space-y-3">
                  <Label htmlFor="documentFile">Select File</Label>
                  <Input
                    id="documentFile"
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setDocumentName(e.target.files[0].name);
                      }
                    }}
                    className="cursor-pointer"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.zip,.rar"
                  />
                  <p className="text-xs text-muted-foreground">
                    Supported formats: PDF, Office documents, images, and archives up to 10 MB.
                  </p>
                  
                  {fileInputRef.current?.files && fileInputRef.current.files.length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Size: {formatFileSize(fileInputRef.current.files[0].size)}
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <Label htmlFor="fileDescription">Description (Optional)</Label>
                  <Textarea
                    id="fileDescription"
                    value={documentDescription}
                    onChange={(e) => setDocumentDescription(e.target.value)}
                    placeholder="Add details about this document..."
                    className="resize-none"
                    rows={3}
                  />
                </div>

                {uploadProgress > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs">Upload Progress</Label>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="url" className="space-y-5">
                <div className="space-y-3">
                  <Label htmlFor="documentName">Document Name</Label>
                  <Input
                    id="documentName"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    placeholder="e.g. Business Proposal"
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="documentUrl">Document URL</Label>
                  <Input
                    id="documentUrl"
                    value={documentUrl}
                    onChange={(e) => setDocumentUrl(e.target.value)}
                    placeholder="https://example.com/document.pdf"
                  />
                  <p className="text-xs text-muted-foreground">
                    Provide a link to your document. Supported formats include PDF, images, and more.
                  </p>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="documentDescription">Description (Optional)</Label>
                  <Textarea
                    id="documentDescription"
                    value={documentDescription}
                    onChange={(e) => setDocumentDescription(e.target.value)}
                    placeholder="Add details about this document..."
                    className="resize-none"
                    rows={3}
                  />
                </div>
              </TabsContent>
              </Tabs>
              
              {uploadError && (
                <div className="text-sm text-destructive mt-4">
                  Error: {uploadError}
                </div>
              )}
            </DialogBody>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleDialogClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleSubmitDocument}
                disabled={isSubmitting || !isFormValid()}
                className={cn(
                  "min-w-[80px]",
                  isSubmitting && "opacity-80"
                )}
              >
                {isSubmitting ? "Adding..." : "Add Document"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {documents.length > 0 ? (
          <div className="space-y-2">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-start justify-between p-2 md:p-3 bg-muted/50 rounded-md">
                <div className="flex items-start min-w-0 space-x-3">
                  <div className="mr-2 md:mr-3 p-1 md:p-2 bg-primary/10 rounded-md">
                    {getDocumentIcon(doc.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-xs md:text-sm truncate">{doc.name}</p>
                    {doc.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{doc.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(doc.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                  <a 
                    href={doc.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary hover:underline text-xs md:text-sm rounded-md px-2 py-1 hover:bg-primary/10"
                  >
                    View
                  </a>
                  <DeleteConfirmationDialog
                    entityName={doc.name}
                    onConfirm={() => handleDeleteDocument(doc.id)}
                    trigger={
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground p-3 md:p-4 text-xs md:text-sm">
            No documents yet. Add your first document.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentsTabContent;
